import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import api from 'app/utils/axiosInstance';
import { userLoggedIn } from 'app/redux/auth/authSlice';
import { colors } from 'app/utils/colors';

export default function TwoFactorVerify() {
  const [digits, setDigits]   = useState(['', '', '', '', '', '']);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake]     = useState(false);
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const next = [...digits];
    next[index] = value;
    setDigits(next);
    setError('');
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
    if (e.key === 'Enter') handleVerify();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!pasted) return;
    const next = [...digits];
    pasted.split('').forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    inputRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    const token = digits.join('');
    if (token.length !== 6) {
      setError('Enter the 6-digit code from Google Authenticator.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const userId = sessionStorage.getItem('pending_2fa_userId');
      const { data } = await api.post('/auth/login-2fa', { userId, token });

      const accessToken = data?.access_token || data?.accessToken || data?.token;
      const user = data?.user || null;

      sessionStorage.removeItem('pending_2fa_userId');
      if (accessToken) {
        const authData = { accessToken, user };
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('auth_user', JSON.stringify(authData));
        dispatch(userLoggedIn(authData));
      }
      setSuccess(true);
      setTimeout(() => navigate('/'), 2200);
    } catch {
      setError('Invalid code. Check Google Authenticator and try again.');
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    } finally {
      setLoading(false);
    }
  };

  const filled = digits.filter(Boolean).length;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

        * { box-sizing: border-box; }

        .tfa-page {
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${colors.backgroundLight};
          font-family: 'Inter', sans-serif;
          padding: 18px;
        }

        .tfa-card {
          max-width: 440px;
          width: 100%;
          background: #ffffff;
          border-radius: 20px;
          padding: 44px 40px 40px;
          box-shadow: 0 2px 24px rgba(0,0,0,0.06);
          /* keep card height stable during transition */
          min-height: 540px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }

        /* ── top phone icon circle ── */
        .tfa-phone-circle {
          width: 88px;
          height: 88px;
          border-radius: 50%;
          background: ${colors.backgroundLight};
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 28px;
        }

        .tfa-phone-circle svg {
          width: 44px;
          height: 44px;
          color: ${colors.primary};
        }

        .tfa-title {
          font-size: 22px;
          font-weight: 700;
          color: #1a1a1a;
          text-align: center;
          letter-spacing: -0.3px;
          margin: 0 0 10px;
        }

        .tfa-sub {
          font-size: 14px;
          color: #888;
          text-align: center;
          line-height: 1.65;
          margin: 0 0 28px;
        }

        .tfa-sub strong {
          color: #444;
          font-weight: 600;
        }

        /* ── GA badge ── */
        .tfa-badge {
          display: flex;
          align-items: center;
          gap: 12px;
          background: ${colors.backgroundLight};
          border-radius: 12px;
          padding: 14px 16px;
          margin-bottom: 28px;
          width: 100%;
        }

        .tfa-badge-icon-circle {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 1px 4px rgba(0,0,0,0.08);
        }

        .tfa-badge-icon-circle svg {
          width: 20px;
          height: 20px;
          color: ${colors.primary};
        }

        .tfa-badge-text b {
          display: block;
          font-size: 13.5px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 2px;
        }

        .tfa-badge-text span {
          font-size: 12.5px;
          color: #888;
          line-height: 1.4;
        }

        /* ── progress bars ── */
        .tfa-progress {
          display: flex;
          gap: 6px;
          margin-bottom: 16px;
          width: 100%;
        }

        .tfa-bar {
          flex: 1;
          height: 3px;
          border-radius: 2px;
          background: #ebebeb;
          transition: background 0.2s;
        }

        .tfa-bar.on {
          background: ${colors.primary};
        }

        /* ── digit inputs ── */
        .tfa-digits {
          display: flex;
          gap: 8px;
          justify-content: center;
          margin-bottom: 10px;
        }

        .tfa-digits.shake {
          animation: tfashake 0.42s ease;
        }

        @keyframes tfashake {
          0%,100% { transform: translateX(0); }
          20%      { transform: translateX(-7px); }
          40%      { transform: translateX(7px); }
          60%      { transform: translateX(-4px); }
          80%      { transform: translateX(4px); }
        }

        .tfa-digit-input {
          width: 56px;
          height: 64px;
          background: ${colors.backgroundLight};
          border: 1.5px solid transparent;
          border-radius: 12px;
          font-size: 26px;
          font-weight: 700;
          color: #1a1a1a;
          text-align: center;
          outline: none;
          caret-color: transparent;
          font-family: 'Inter', monospace;
          transition: border-color 0.18s, box-shadow 0.18s, transform 0.15s;
        }

        .tfa-digit-input:focus {
          border-color: ${colors.primary};
          box-shadow: 0 0 0 3px ${colors.backgroundLight};
          transform: translateY(-2px);
          background: #fff;
        }

        .tfa-digit-input.has-val {
          border-color: ${colors.primary};
          background: #fff;
          color: ${colors.primary};
        }

        .tfa-digit-input.is-err {
          border-color: #f44336;
          box-shadow: 0 0 0 3px rgba(244,67,54,0.10);
        }

        .tfa-hint {
          font-size: 12px;
          color: #bbb;
          text-align: center;
          margin: 0 0 24px;
        }

        /* ── error box ── */
        .tfa-error {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          background: rgba(244,67,54,0.06);
          border: 1px solid rgba(244,67,54,0.18);
          border-radius: 10px;
          padding: 10px 13px;
          font-size: 13px;
          color: #f44336;
          margin-bottom: 18px;
          line-height: 1.45;
          width: 100%;
        }

        .tfa-error svg {
          flex-shrink: 0;
          width: 15px;
          height: 15px;
          margin-top: 1px;
        }

        /* ── button ── */
        .tfa-btn {
          width: 100%;
          padding: 16px;
          background: ${colors.primary};
          color: #fff;
          border: none;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          transition: opacity 0.18s, transform 0.15s;
        }

        .tfa-btn:hover:not(:disabled) {
          opacity: 0.88;
          transform: translateY(-1px);
        }

        .tfa-btn:active:not(:disabled) { transform: translateY(0); }

        .tfa-btn:disabled {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .tfa-spinner {
          width: 15px;
          height: 15px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.65s linear infinite;
        }

        @keyframes spin { to { transform: rotate(360deg); } }

        /* ── divider + footer ── */
        .tfa-divider {
          height: 1px;
          background: #f0f0f0;
          margin: 24px 0;
          width: 100%;
        }

        .tfa-footer {
          text-align: center;
          font-size: 13px;
          color: #aaa;
        }

        .tfa-footer a {
          color: ${colors.primary};
          text-decoration: none;
          font-weight: 500;
          cursor: pointer;
        }

        .tfa-footer a:hover { text-decoration: underline; }

        /* ── success state (Lottie) ── */
        .tfa-success {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 100%;
          animation: spop 0.45s cubic-bezier(.34,1.56,.64,1) forwards;
        }

        @keyframes spop {
          from { transform: scale(0.88); opacity: 0; }
          to   { transform: scale(1);    opacity: 1; }
        }

        .tfa-lottie-wrap {
          width: 180px;
          height: 180px;
          margin-bottom: 24px;
        }

        .tfa-success h3 {
          font-size: 20px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 6px;
          text-align: center;
        }

        .tfa-success p {
          font-size: 14px;
          color: #888;
          margin: 0;
          text-align: center;
        }

        /* ── form wrapper so we can crossfade ── */
        .tfa-form {
          display: flex;
          flex-direction: column;
          align-items: center;
          width: 100%;
        }
      `}</style>

      <div className="tfa-page">
        <div className="tfa-card">

          {success ? (
            <div className="tfa-success">
              <div className="tfa-lottie-wrap">
  <Lottie
    path="/assets/json/identity-confirmed2.json"
    loop={false}
    autoplay={true}
  />
</div>
              <h3>Identity confirmed</h3>
              <p>Signing you in…</p>
            </div>
          ) : (
            <div className="tfa-form">
              {/* ── phone / authenticator icon ── */}
              <div className="tfa-phone-circle">
                <span
                  style={{
                    width: 40,
                    height: 40,
                    backgroundColor: colors.primary,
                    WebkitMaskImage: 'url("/assets/icons/settingsIcons/code.svg")',
                    maskImage: 'url("/assets/icons/settingsIcons/code.svg")',
                    WebkitMaskRepeat: "no-repeat",
                    maskRepeat: "no-repeat",
                    WebkitMaskSize: "contain",
                    maskSize: "contain",
                    WebkitMaskPosition: "center",
                    maskPosition: "center",
                    display: "inline-block",
                  }}
                />
              </div>

              <h2 className="tfa-title">Two-step verification</h2>
              <p className="tfa-sub">
                Your account is protected with{' '}
                <strong>Google Authenticator</strong>.<br />
                Enter the code shown in the app.
              </p>

              {/* ── GA badge ── */}
              <div className="tfa-badge">
                <div className="tfa-badge-icon-circle">
                  <span
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: colors.primary,
                      WebkitMaskImage: 'url("/assets/icons/settingsIcons/secure.svg")',
                      maskImage: 'url("/assets/icons/settingsIcons/secure.svg")',
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                      WebkitMaskSize: "contain",
                      maskSize: "contain",
                      WebkitMaskPosition: "center",
                      maskPosition: "center",
                      display: "inline-block",
                    }}
                  />
                </div>
                <div className="tfa-badge-text">
                  <b>Google Authenticator</b>
                  <span>Open the app and find the 6-digit code for this account.</span>
                </div>
              </div>

              {/* ── progress bars ── */}
              <div className="tfa-progress">
                {digits.map((d, i) => (
                  <div key={i} className={`tfa-bar${d ? ' on' : ''}`} />
                ))}
              </div>

              {/* ── 6 digit inputs ── */}
              <div
                className={`tfa-digits${shake ? ' shake' : ''}`}
                onPaste={handlePaste}
              >
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={el => inputRefs.current[i] = el}
                    className={`tfa-digit-input${d ? ' has-val' : ''}${error ? ' is-err' : ''}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={d}
                    onChange={e => handleChange(i, e.target.value)}
                    onKeyDown={e => handleKeyDown(i, e)}
                    autoComplete="off"
                  />
                ))}
              </div>

              <p className="tfa-hint">You can also paste the code directly</p>

              {error && (
                <div className="tfa-error">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  {error}
                </div>
              )}

              <button
                className="tfa-btn"
                onClick={handleVerify}
                disabled={loading || filled < 6}
              >
                {loading
                  ? <><div className="tfa-spinner" />Verifying…</>
                  : 'Confirm identity'
                }
              </button>

              <div className="tfa-divider" />

              <div className="tfa-footer">
                Lost access to your authenticator?{' '}
                <a>Get help</a>
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}